'use client';

import * as React from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { cn } from '@/app/lib/utils';
import Spinner from './Spinner';
import Icon from './Icon';
import { Button } from './Button';

interface ScrollAreaProps extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  orientation?: 'vertical' | 'horizontal' | 'both';
  rootClassName?: string;
  viewportClassName?: string;
  scrollbarClassName?: string;
}

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(
  (
    {
      children,
      className,
      orientation = 'vertical',
      rootClassName,
      viewportClassName,
      scrollbarClassName,
      ...props
    },
    ref
  ) => (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn('relative overflow-hidden w-full h-full', rootClassName, className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        className={cn(
          'h-full w-full rounded-[inherit] [&>div]:!min-w-full [&>div]:!block [&>div]:min-h-full [&>div]:rounded-[inherit]',
          orientation === 'vertical' &&
            '[&>div]:!w-full [&>div]:!max-w-full [&>div]:!min-w-0 [&>div]:overflow-x-clip',
          orientation === 'horizontal' && 'pb-3',
          orientation === 'vertical' && 'pr-3',
          viewportClassName
        )}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      {orientation === 'vertical' || orientation === 'both' ? (
        <ScrollBar orientation="vertical" className={scrollbarClassName} />
      ) : null}
      {orientation === 'horizontal' || orientation === 'both' ? (
        <ScrollBar orientation="horizontal" className={scrollbarClassName} />
      ) : null}
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    onClick={e => {
      e.stopPropagation();
      e.preventDefault();
    }}
    className={cn(
      'flex touch-none select-none transition-colors !bg-transparent z-50',
      orientation === 'vertical' && 'h-full w-2.5 border-l border-l-transparent p-[1px]',
      orientation === 'horizontal' && 'h-2.5 flex-col border-t border-t-transparent p-[1px]',
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border transition-colors" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

interface CustomScrollProps {
  children: React.ReactNode;
  className?: string;
  rootClassName?: string;
  viewportClassName?: string;
  scrollbarClassName?: string;
  orientation?: 'vertical' | 'horizontal' | 'both';
  type?: 'auto' | 'always' | 'scroll' | 'hover';
  scrollHideDelay?: number;
  height?: string | number;
  maxHeight?: string | number;
  minHeight?: string | number;
  width?: string | number;
  maxWidth?: string | number;
  minWidth?: string | number;
  infiniteScroll?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
  loadingContent?: React.ReactNode;
  showScrollToTop?: boolean;
  scrollToTopThreshold?: number;
}

const CustomScroll = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  CustomScrollProps
>(
  (
    {
      children,
      className,
      rootClassName,
      viewportClassName,
      scrollbarClassName,
      orientation = 'vertical',
      type = 'hover',
      scrollHideDelay = 300,
      height,
      maxHeight,
      minHeight,
      width,
      maxWidth,
      minWidth,
      infiniteScroll = false,
      onLoadMore,
      hasMore = false,
      loading = false,
      loadingContent,
      showScrollToTop = false,
      scrollToTopThreshold = 200,
      ...props
    },
    ref
  ) => {
    const [showScrollToTopButton, setShowScrollToTopButton] = React.useState(false);
    const scrollAreaRef = React.useRef<HTMLDivElement>(null);
    const loadMoreTriggerRef = React.useRef<HTMLDivElement>(null);

    const style = {
      ...(height && { height: typeof height === 'number' ? `${height}px` : height }),
      ...(maxHeight && { maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight }),
      ...(minHeight && { minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight }),
      ...(width && { width: typeof width === 'number' ? `${width}px` : width }),
      ...(maxWidth && { maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth }),
      ...(minWidth && { minWidth: typeof minWidth === 'number' ? `${minWidth}px` : minWidth }),
    };

    React.useEffect(() => {
      if (!infiniteScroll || !onLoadMore || !hasMore || loading) return;

      const scrollArea = scrollAreaRef.current;
      const viewport = scrollArea?.querySelector(
        '[data-radix-scroll-area-viewport]'
      ) as HTMLElement;

      const observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && !loading && hasMore) {
            onLoadMore();
          }
        },
        {
          root: viewport || null,
          threshold: 0.1,
          rootMargin: '100px',
        }
      );

      if (loadMoreTriggerRef.current) {
        observer.observe(loadMoreTriggerRef.current);
      }

      return () => {
        observer.disconnect();
      };
    }, [infiniteScroll, onLoadMore, hasMore, loading]);

    React.useEffect(() => {
      if (!showScrollToTop) return;

      const handleScroll = () => {
        if (scrollAreaRef.current) {
          const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
          if (viewport) {
            const { scrollTop } = viewport;
            setShowScrollToTopButton(scrollTop > scrollToTopThreshold);
          }
        }
      };

      const scrollArea = scrollAreaRef.current;
      if (scrollArea) {
        const viewport = scrollArea.querySelector('[data-radix-scroll-area-viewport]');
        if (viewport) {
          viewport.addEventListener('scroll', handleScroll);
          return () => viewport.removeEventListener('scroll', handleScroll);
        }
      }
    }, [showScrollToTop, scrollToTopThreshold]);

    const scrollToTop = () => {
      if (scrollAreaRef.current) {
        const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (viewport) {
          viewport.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    };

    return (
      <div className="relative w-full h-full group/scroll">
        <ScrollArea
          ref={node => {
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
            scrollAreaRef.current = node;
          }}
          className={cn('w-full h-full', className)}
          rootClassName={rootClassName}
          viewportClassName={viewportClassName}
          scrollbarClassName={scrollbarClassName}
          type={type}
          scrollHideDelay={scrollHideDelay}
          orientation={orientation}
          style={Object.keys(style).length > 0 ? style : undefined}
          {...props}
        >
          {children}

          {infiniteScroll && hasMore && <div ref={loadMoreTriggerRef} className="h-1 w-full" />}

          {infiniteScroll &&
            loading &&
            hasMore &&
            (loadingContent || (
              <div className="flex items-center justify-center py-4">
                <Spinner size={8} className="relative" />
              </div>
            ))}
        </ScrollArea>

        {showScrollToTop && showScrollToTopButton && (
          <Button
            size="icon"
            className="absolute bottom-4 right-4 z-10 shadow-lg rounded-full group-hover/scroll:flex hidden bg-[#000]/10 text-[#000] hover:bg-[#000] hover:text-white transition-colors"
            onClick={scrollToTop}
          >
            <Icon name="arrow-up" size={5} />
          </Button>
        )}
      </div>
    );
  }
);

CustomScroll.displayName = 'CustomScroll';

export { CustomScroll, ScrollArea, ScrollBar };
